import React from "react";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImageValidateSize from "filepond-plugin-image-validate-size";
import FilePondPluginImageCrop from "filepond-plugin-image-crop";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginImageResize from "filepond-plugin-image-resize";
import FilePondPluginImageTransform from "filepond-plugin-image-transform";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import { useSnackbar } from "notistack";
import { useMyEntitlements, GameFiles } from "@seabrookstudios/pitch2table";
import { useQueryClient } from "@tanstack/react-query";
import { useUploadGameAsset } from "./uploadGameAsset";

registerPlugin(FilePondPluginImagePreview);
registerPlugin(FilePondPluginImageValidateSize);
registerPlugin(FilePondPluginImageCrop);
registerPlugin(FilePondPluginImageResize);
registerPlugin(FilePondPluginImageTransform);
registerPlugin(FilePondPluginFileValidateSize);

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {(url: string) => Promise<void>} [props.onUploadComplete]
 */
const UploadAssetComponent = ({ gameId, onUploadComplete }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const onFileUploadSuccess = async (url) => {
    await queryClient.invalidateQueries({ queryKey: GameFiles(gameId) });

    enqueueSnackbar("Asset uploaded", { variant: "success" });
    onUploadComplete?.(url);
  };

  const onFileUploadFailure = () => {
    enqueueSnackbar("An error occurred uploading asset", { variant: "error" });
  };

  const uploadImage = useUploadGameAsset(gameId, onFileUploadSuccess, onFileUploadFailure);

  return (
    <FilePond
      allowMultiple
      allowReplace={false}
      allowRevert={false}
      allowProcess={false}
      credits={false}
      instantUpload={true}
      maxFiles={10}
      labelIdle={'Drag & Drop your files or <span class="filepond--label-action"> Browse </span>'}
      maxFileSize="5MB"
      server={{
        process: uploadImage,
      }}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {(url: string) => Promise<void>} [props.onUploadComplete]
 */
const PremiumUploadAssetComponent = ({ gameId, onUploadComplete }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const onFileUploadSuccess = async (url) => {
    await queryClient.invalidateQueries({ queryKey: GameFiles(gameId) });

    enqueueSnackbar("Asset uploaded", { variant: "success" });
    onUploadComplete?.(url);
  };

  const onFileUploadFailure = () => {
    enqueueSnackbar("An error occurred uploading asset", { variant: "error" });
  };

  const uploadImage = useUploadGameAsset(gameId, onFileUploadSuccess, onFileUploadFailure);

  return (
    <FilePond
      allowFileTypeValidation={false}
      allowMultiple
      allowReplace={false}
      allowRevert={false}
      allowProcess={false}
      credits={false}
      instantUpload={true}
      maxFiles={10}
      maxParallelUploads={1}
      labelIdle={'Drag & Drop your files or <span class="filepond--label-action"> Browse </span>'}
      maxFileSize="500MB"
      server={{
        process: uploadImage,
      }}
    />
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {(url: string) => Promise<void>} [props.onUploadComplete]
 */
export const UploadAsset = ({ gameId, onUploadComplete }) => {
  const { data: entitlements } = useMyEntitlements();

  const hasLargeFileUploads = entitlements && entitlements.hasLargeFileUploads;

  return hasLargeFileUploads ? (
    <PremiumUploadAssetComponent gameId={gameId} onUploadComplete={onUploadComplete} />
  ) : (
    <UploadAssetComponent gameId={gameId} onUploadComplete={onUploadComplete} />
  );
};
